import { studentPageWindow } from "@classroom/domain";
import { useState, useSyncExternalStore } from "react";
import { useTranslation } from "./i18n";
import "./student-pagination.css";

export const studentPageSize = 10;

const studentCardQueries = [
  { query: "(min-width: 1600px) and (hover: hover) and (pointer: fine)", size: 30 },
  { query: "(min-width: 1200px) and (hover: hover) and (pointer: fine)", size: 20 },
];

function subscribeCardPageSize(notify: () => void) {
  const queries = studentCardQueries.map(({ query }) => window.matchMedia(query));
  for (const query of queries) query.addEventListener("change", notify);
  return () => {
    for (const query of queries) query.removeEventListener("change", notify);
  };
}

function cardPageSize() {
  return studentCardQueries.find(({ query }) => window.matchMedia(query).matches)?.size || studentPageSize;
}

export function useStudentCardPageSize() {
  return useSyncExternalStore(subscribeCardPageSize, cardPageSize, () => studentPageSize);
}

// Keep the full collection in the caller for saves, totals and exports.
export function useStudentPagination<T>(items: T[], scope: string, pageSize = studentPageSize) {
  const [position, setPosition] = useState({ scope, page: 1, pageSize });
  const { page, pages, start, end } = studentPageWindow(position, scope, items.length, pageSize);
  if (position.scope !== scope || position.page !== page || position.pageSize !== pageSize)
    setPosition({ scope, page, pageSize });
  return {
    items: items.slice(start, end),
    page,
    pages,
    setPage: (next: number) => setPosition({ scope, page: Math.max(1, Math.min(next, pages)), pageSize }),
  };
}

export default function StudentPagination({
  page,
  pages,
  setPage,
  label = "records",
  disabled = false,
}: {
  page: number;
  pages: number;
  setPage: (page: number) => void;
  label?: "records" | "directory" | "routine" | "memos" | "unseated" | "submissions";
  disabled?: boolean;
}) {
  const { t } = useTranslation("studentPagination");
  if (pages <= 1) return null;
  const numbers = Array.from({ length: pages }, (_, index) => index + 1).filter(
    (number) => number === 1 || number === pages || Math.abs(number - page) <= 1,
  );
  return (
    <nav className="student-pagination" aria-label={t(label)}>
      <button type="button" disabled={disabled || page === 1} onClick={() => setPage(page - 1)}>
        {t("previous")}
      </button>
      {numbers.map((number, index) => (
        <span key={number}>
          {index > 0 && number > numbers[index - 1] + 1 && (
            <span className="pagination-gap" aria-hidden="true">
              …
            </span>
          )}
          <button
            type="button"
            disabled={disabled}
            aria-label={t("page", { page: number })}
            aria-current={number === page ? "page" : undefined}
            onClick={() => setPage(number)}
          >
            {number}
          </button>
        </span>
      ))}
      <button type="button" disabled={disabled || page === pages} onClick={() => setPage(page + 1)}>
        {t("next")}
      </button>
      <span className="pagination-position" aria-live="polite" aria-atomic="true">
        {t("position", { page, pages })}
      </span>
    </nav>
  );
}
